// Client phone numbers need at least 7 digits (Roadmap 1803). The box stays
// optional: an empty phone is fine.
export const PHONE_MIN_DIGITS = 7;
/** E.164: at most 15 digits including the country code. */
export const PHONE_MAX_DIGITS = 15;

export const PHONE_TOO_SHORT = `Phone number must have at least ${PHONE_MIN_DIGITS} digits.`;
export const PHONE_TOO_LONG = `Phone number can have at most ${PHONE_MAX_DIGITS} digits.`;

/** The error to show for one phone box, or "" when it is fine. */
export const phoneNumberProblem = (value?: string | null): string => {
  const digits = (value || "").replace(/\D/g, "");
  if (!digits) return "";
  if (digits.length < PHONE_MIN_DIGITS) return PHONE_TOO_SHORT;
  return digits.length > PHONE_MAX_DIGITS ? PHONE_TOO_LONG : "";
};

/** The first problem among several phone boxes, or "". */
export const phoneNumbersProblem = (
  values: readonly (string | null | undefined)[],
): string => values.map(phoneNumberProblem).find(Boolean) || "";

const formatNorthAmerican = (digits: string): string => {
  const d = digits.slice(0, 10);
  if (d.length < 4) {
    return d.length ? `(${d} ` : "";
  }
  if (d.length < 7) {
    return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  }
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6, 10)}`;
};

/**
 * Phone box formatting while typing (QA batch 12, 2026-10-21). North American
 * numbers become "(416) 555-1234"; a leading country code 1 ("+1", "1 ") is
 * dropped first (area codes never start with 1). Before, "+1 (416) 555-1234"
 * was cut to the first 10 digits = "(141) 655-5123". Other international
 * numbers ("+44 20 7946 0958") are kept as typed: digits, spaces and ( ) - .
 * after the "+", max 20 characters.
 */
export const formatPhoneInput = (value: string): string => {
  const text = (value || "").trimStart();
  if (text.startsWith("+")) {
    const rest = text.slice(1).replace(/[^\d ()./-]/g, "");
    const digits = rest.replace(/\D/g, "");
    if (digits.startsWith("1")) {
      // Still typing "+1": keep it on screen.
      if (digits.length === 1) return "+1";
      return formatNorthAmerican(digits.slice(1));
    }
    return ("+" + rest.replace(/\s+/g, " ")).slice(0, 20);
  }
  const digits = text.replace(/\D/g, "");
  return formatNorthAmerican(digits.startsWith("1") ? digits.slice(1) : digits);
};
